import React, { useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';

import Dropdown from '../../../components/Dropdown';
import { Input, DatePicker } from 'antd';

import { UNIVERSITY_IDS } from '../.././../common/accountIds';

const TextQuestion = (props) => {
    return (
        <label className="sign-up-form-label">
            <legend className="modal-label-text">
                {props.required ? <span className="required-mark">*</span> : null}
                {props.question}
            </legend>
            <Input
                name={props.name}
                type="text"
                className="modal-input"
                value={props.answer}
                onChange={(e) => props.onChange(props.name, props.type, e.target.value, props.required)}
                aria-label={props.question}
                aria-required="false"
                required={props.required}
            />
            {props.error ? <span className="error">{props.error}</span> : null}
        </label>
    );
};

const BooleanQuestion = (props) => {
    return (
        <>
            <fieldset className="optional-question-fieldset">
                <legend className="modal-label-text">
                    {props.required ? <span className="required-mark">*</span> : null}
                    {props.question}
                </legend>
                <div className="radio-row">
                    <label className="radio-label">
                        <Input
                            type="radio"
                            className="radio-input"
                            value="Yes"
                            name={props.name}
                            checked={props.answer === 'Yes'}
                            onChange={() => props.onChange(props.name, props.type, 'Yes', props.required)}
                            aria-label="Answer yes"
                            aria-required="false"
                            required={props.required}
                        />
                        <span className="radio-text">Yes</span>
                    </label>
                    <label className="radio-label">
                        <Input
                            type="radio"
                            className="radio-input"
                            value="No"
                            name={props.name}
                            checked={props.answer === 'No'}
                            onChange={() => props.onChange(props.name, props.type, 'No', props.required)}
                            aria-label="Answer no"
                            aria-required="false"
                            required={props.required}
                        />
                        <span className="radio-text">No</span>
                    </label>
                </div>
                {props.error ? <span className="error">{props.error}</span> : null}
            </fieldset>
        </>
    );
};

const DateQuestion = (props) => {
    return (
        <>
            <fieldset className="optional-question-fieldset">
                <legend className="modal-label-text">
                    {props.required ? <span className="required-mark">*</span> : null}
                    {props.question}
                </legend>
                <div className="radio-row">
                    <DatePicker
                        selected={props.answer}
                        onChange={(date) => props.onChange(props.name, props.type, date, props.required)}
                        placeholderText="DD.MM.YYYY"
                        format="DD.MM.YYYY"
                        disabledDate={(d) => !d || d.isBefore(props.minimumAge, 'years')}
                        className="date-picker-antd"
                    />
                </div>
                {props.error ? <span className="error">{props.error}</span> : null}
            </fieldset>
        </>
    );
};

const DropdownQuestion = (props) => {
    return (
        <>
            <legend className="modal-label-text">
                {props.required ? <span className="required-mark">*</span> : null}
                {props.question}
            </legend>
            <label className="label sign-up-form-label">
                <div className="control">
                    <Dropdown
                        items={props.options}
                        selectedItem={props.answer}
                        type={props.optionType}
                        onSelect={(value) => props.onChange(props.name, props.type, value, props.required)}
                        required={props.required}
                    />
                </div>
                {props.error ? <span className="error">{props.error}</span> : null}
            </label>
        </>
    );
};

const OptionsQuestion = (props) => {
    const options = props.options.map((item, index) => (
        <label className="radio-label" key={index}>
            <Input
                type="radio"
                className="radio-input"
                value={item.id}
                name={props.name}
                checked={props.answer === item.id}
                onChange={() => props.onChange(props.name, props.type, item.id, props.required)}
                aria-label={`Select an option ${item.name}`}
                aria-required="false"
                required={props.required}
            />
            <span className="radio-text">{item.name}</span>
        </label>
    ));

    return (
        <>
            <fieldset className="optional-question-fieldset">
                <legend className="modal-label-text">
                    {props.required ? <span className="required-mark">*</span> : null}
                    {props.question}
                </legend>
                <div className="modal-label">
                    <div className="radio-row">{options}</div>
                </div>
                {props.error ? <span className="error">{props.error}</span> : null}
            </fieldset>
        </>
    );
};

const OptionalQuestions = ({
    answers: answersProps,
    data,
    university,
    universityId,
    minimumAge,
    errors,
    onFieldUpdate,
}) => {
    const [answers, setAnswers] = useState(answersProps || {});

    const handleFieldUpdate = useCallback(
        (questionId, questionType, answer, isMandatory) => {
            let answerData = {};
            for (const item of data) {
                if (item.id === questionId) {
                    if (item.key === 'custom') {
                        answerData = {
                            question_key: 'custom',
                            question: item.question,
                            answer_type: item.answer_type,
                            answer: answer,
                        };
                    } else {
                        answerData = {
                            question_key: item.key,
                            answer: answer,
                        };
                    }
                }
            }
            const rawAnswer = { [questionId]: answerData };

            const updatedAnswers = Object.assign({}, answers, rawAnswer);
            setAnswers(updatedAnswers);
            let formattedAnswer = answer;
            switch (questionType) {
                case 'postcode':
                case 'school':
                case 'start_study':
                case 'custom':
                    break;

                case 'already_applied':
                    formattedAnswer = answer === 'Yes';
                    break;

                case 'birthday':
                    formattedAnswer = dayjs(answer).format('YYYY-MM-DD HH:mm:ss');
                    break;

                case 'residence_country':
                case 'specialization':
                    formattedAnswer = answer.id;
                    break;
            }

            onFieldUpdate(
                { [questionId]: { ...answerData, answer: formattedAnswer } },
                formattedAnswer,
                rawAnswer,
                questionType,
                questionId,
                isMandatory
            );
        },
        [answers, data, onFieldUpdate]
    );

    const handleQuestionsOrder = useCallback((questions, universityId) => {
        switch (universityId) {
            // Change question index for Griffith University
            case UNIVERSITY_IDS.GRIFFITH:
            case UNIVERSITY_IDS.TEST_UNI_2: {
                const countriesIndex = questions.findIndex((item) => item.key === 'residence_country');
                const specializationsIndex = questions.findIndex((item) => item.key === 'specialization');
                const result = [];

                // Set countries to the first place:
                if (countriesIndex !== -1) {
                    result.push(questions[countriesIndex]);
                }

                // Set specializations to the first place but after countries:
                if (specializationsIndex !== -1) {
                    result.push(questions[specializationsIndex]);
                }

                // Add all other questions:
                for (const item of questions) {
                    if (item.key !== 'residence_country' && item.key !== 'specialization') {
                        result.push(item);
                    }
                }

                return result;
            }
            default:
                return questions;
        }
    }, []);

    const renderQuestions = useMemo(() => {
        if (!data || !data.length) {
            return null;
        }

        const orderedData = handleQuestionsOrder(data, universityId);

        const questions = orderedData.map((item) => {
            let question = null;
            switch (item.key) {
                case 'postcode':
                case 'school':
                    question = (
                        <TextQuestion
                            key={item.id}
                            name={item.id}
                            type={item.key}
                            question={item.question}
                            answer={answers[item.id] ? answers[item.id].answer : ''}
                            onChange={handleFieldUpdate}
                            error={errors && errors[item.id]}
                            required={item.isMandatory}
                        />
                    );
                    break;

                case 'already_applied':
                    if (item.key === 'applied' && !item.question.endsWith('?')) {
                        item.question += university.name + '?';
                    }
                    question = (
                        <BooleanQuestion
                            key={item.id}
                            name={item.id}
                            type={item.key}
                            question={item.question}
                            answer={answers[item.id] ? answers[item.id].answer : ''}
                            onChange={handleFieldUpdate}
                            error={errors && errors[item.id]}
                            required={item.isMandatory}
                        />
                    );
                    break;

                case 'birthday':
                    question = (
                        <DateQuestion
                            key={item.id}
                            name={item.id}
                            type={item.key}
                            question={item.question}
                            answer={answers[item.id] ? answers[item.id].answer : ''}
                            minimumAge={minimumAge}
                            onChange={handleFieldUpdate}
                            error={errors && errors[item.id]}
                            required={item.isMandatory}
                        />
                    );
                    break;

                case 'residence_country':
                    question = (
                        <DropdownQuestion
                            key={item.id}
                            name={item.id}
                            type={item.key}
                            question={item.question}
                            answer={answers[item.id] ? answers[item.id].answer : { name: '' }}
                            options={
                                universityId === UNIVERSITY_IDS.INTAKE
                                    ? item.predefined_answers.slice().filter((country) => country.name !== 'Niger')
                                    : item.predefined_answers
                            }
                            optionType="country"
                            onChange={handleFieldUpdate}
                            error={
                                universityId === UNIVERSITY_IDS.GRIFFITH || universityId === UNIVERSITY_IDS.TEST_UNI_2
                                    ? errors.country
                                    : errors && errors[item.id]
                            }
                            required={
                                universityId === UNIVERSITY_IDS.GRIFFITH || universityId === UNIVERSITY_IDS.TEST_UNI_2
                                    ? true
                                    : item.isMandatory
                            }
                        />
                    );
                    break;

                case 'specialization':
                    question = (
                        <DropdownQuestion
                            key={item.id}
                            name={item.id}
                            type={item.key}
                            question={item.question}
                            answer={answers[item.id] ? answers[item.id].answer : { name: '' }}
                            options={item.predefined_answers.slice().sort((a, b) => a.name.localeCompare(b.name))}
                            optionType="course"
                            onChange={handleFieldUpdate}
                            error={errors && errors[item.id]}
                            required={item.isMandatory}
                        />
                    );
                    break;

                case 'start_study':
                    question = (
                        <OptionsQuestion
                            key={item.id}
                            name={item.id}
                            type={item.key}
                            question={item.question}
                            answer={answers[item.id] ? answers[item.id].answer : ''}
                            options={item.predefined_answers}
                            onChange={handleFieldUpdate}
                            error={errors && errors[item.id]}
                            required={item.isMandatory}
                        />
                    );
                    break;

                case 'custom':
                    if (item.predefined_answers.length) {
                        question = (
                            <OptionsQuestion
                                key={item.id}
                                name={item.id}
                                type={item.key}
                                question={item.question}
                                answer={answers[item.id] ? answers[item.id].answer : ''}
                                options={item.predefined_answers}
                                onChange={handleFieldUpdate}
                                error={errors && errors[item.id]}
                                required={item.isMandatory}
                            />
                        );
                    } else {
                        question = (
                            <TextQuestion
                                key={item.id}
                                name={item.id}
                                type={item.key}
                                question={item.question}
                                answer={answers[item.id] ? answers[item.id].answer : ''}
                                onChange={handleFieldUpdate}
                                error={errors && errors[item.id]}
                                required={item.isMandatory}
                            />
                        );
                    }
                    break;
            }

            return question;
        });

        return questions;
    }, [answers, data, university, universityId, minimumAge, errors]);

    return <div className="optional-questions-form">{renderQuestions}</div>;
};

export default OptionalQuestions;
